<template>
  <LpBreadcrumbsWrapper
    class="lp-breadcrumb-wrapper"
    data-ua-value="breadcrumbs-wrapper"
    :enabled="wrapper"
  >
    <template #default="slotProps">
      <div
        class="w-full"
        v-bind="slotProps"
        :data-ua-value="slotProps['data-ua-value']"
      >
        <LpBreadcrumbsBackButton
          v-if="back"
          data-ua-value="breadcrumbs-back-button"
          v-bind="backProps"
        />
        <ul
          v-else-if="breadcrumbs"
          class="ly-row breadcrumbs-items-list mb-0"
          data-ua-value="ua-breadcrumbs-items-list"
        >
          <li
            v-for="(breadcrumb, index) in breadcrumbsSorted"
            :key="
              typeof breadcrumb === 'object' ? breadcrumb.label : breadcrumb
            "
            data-ua-value="ua-breadcrumbs-item"
            class="m-0"
          >
            <component
              :is="getBreadcrumbComponent(breadcrumb)"
              v-if="typeof breadcrumb === 'object'"
              :class="
                index !== breadcrumbs.length - 1
                  ? 'text-primary-100'
                  : 'text-black-60'
              "
              v-bind="
                typeof breadcrumb === 'object' && breadcrumbProps(breadcrumb)
              "
              @click="breadcrumb.route && emitRoute(breadcrumb.route)"
            >
              {{
                breadcrumb &&
                typeof breadcrumb === 'object' &&
                'label' in breadcrumb
                  ? breadcrumb.label
                  : breadcrumb
              }}
              <lp-icon
                v-if="index !== breadcrumbs.length - 1"
                name="chevron-right"
                color="black-100"
                class="mx-half"
              />
            </component>
          </li>
        </ul>
        <slot name="title">
          <component
            :is="titleComponent"
            v-if="title"
            data-ua-value="breadcrumb-title"
            :class="['lead font-weight-bold', titleClass]"
          >
            {{ title }}
          </component>
        </slot>
      </div>
    </template>
  </LpBreadcrumbsWrapper>
</template>
<script lang="ts" setup>
import { computed } from 'vue'

import LpBreadcrumbsWrapper from '@/components/breadcrumbs/LpBreadcrumbsWrapper.vue'
import LpBreadcrumbsBackButton from '@/components/breadcrumbs/LpBreadcrumbsBackButton.vue'
import LpIcon from '@/components/icon'

export interface BackProps {
  label: string
  href?: string
  onClick?: () => void
}

export interface BreadcrumbItem {
  order?: number
  label: string
  href?: string
  route?: string
  onClick?: () => void
}

defineOptions({
  name: 'lp-breadcrumbs',
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    /**
     * Includes a back button. It can be a boolean, a string label (which will trigger history.go(-1)), or an object with properties.
     */
    back?: boolean | string | BackProps
    /**
     * The title can be a string or customized using a slot <template #title>.
     */
    title?: string | undefined
    /**
     * If true, the component includes the wrapper and styling; if false, it can be further customized.
     */
    wrapper?: boolean
    /**
     * Breadcrumb items, which can be an array of strings, an object, or undefined if using back.
     */
    breadcrumbs?: string[] | BreadcrumbItem[] | undefined
    /**
     * Component used for the title.
     */
    titleComponent?: string
    /**
     * Class names for the title component.
     */
    titleClass?: string | undefined
  }>(),
  {
    back: false,
    title: undefined,
    wrapper: false,
    breadcrumbs: undefined,
    titleComponent: 'p',
    titleClass: 'text-black-90',
  },
)

defineSlots<{
  title(): unknown
}>()

const emit = defineEmits<{
  (e: 'click', value: string): void
}>()

const backProps = computed((): BackProps => {
  const finalProps: BackProps = {
    label:
      typeof props.back === 'object'
        ? props.back.label
        : typeof props.back === 'boolean'
          ? 'Back'
          : props.back,
  }

  if (props.back && typeof props.back === 'object') {
    if (props.back.href) {
      finalProps.href = props.back.href
    }

    if (props.back.onClick) {
      finalProps.onClick = props.back.onClick
    }
  }

  return finalProps
})

function isBreadcrumbsObject(
  breadcrumbCheck: unknown,
): breadcrumbCheck is Array<BreadcrumbItem> {
  return (
    Array.isArray(breadcrumbCheck) && breadcrumbCheck.every((a) => 'label' in a)
  )
}

const breadcrumbsSorted = computed(() => {
  if (!props.breadcrumbs) return []

  if (typeof props.breadcrumbs.at(0) === 'string') return props.breadcrumbs

  if (
    props.breadcrumbs &&
    Array.isArray(props.breadcrumbs) &&
    props.breadcrumbs.length > 0 &&
    isBreadcrumbsObject(props.breadcrumbs)
  ) {
    if (!props.breadcrumbs.every((a) => typeof a.order === 'number')) {
      return props.breadcrumbs
    }

    return [...props.breadcrumbs].sort((a, b) => {
      const aOrder = Number(a.order)
      const bOrder = Number(b.order)

      return aOrder > bOrder ? 1 : bOrder > aOrder ? -1 : 0
    })
  }

  return props.breadcrumbs
})

function getBreadcrumbComponent(breadcrumbProps: BreadcrumbItem) {
  if (breadcrumbProps.onClick) {
    return 'button'
  }

  return 'a'
}

function breadcrumbProps(breadcrumb: BreadcrumbItem) {
  const finalProps: Omit<BreadcrumbItem, 'label'> = {}

  if (breadcrumb.href) {
    finalProps.href = breadcrumb.href
  }

  if (breadcrumb.onClick) {
    finalProps.onClick = breadcrumb.onClick
  }

  return finalProps
}

function emitRoute(route: string) {
  emit('click', route)
}
</script>
